import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperInstance } from "swiper/types";
import { Modal, ModalBody } from 'reactstrap';

import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { FreeMode, EffectFade, Navigation, Pagination, Thumbs } from "swiper";
import { cloudinaryLoaderVDPImages, cloudinaryLoaderVDPImagesMobile, cloudinaryLoaderVDPThumbnailImages, cloudinaryLoaderVehicleImages } from "utils/cloudinaryUtils";
import { useState, useEffect } from "react";
import dynamic from "next/dynamic";
import useResize from "components/Hooks/useResize";
import { DeviceEnum } from "models/enums";
import { v4 as uuidv4 } from 'uuid';

const AutoImage = dynamic(() => import("components/vehicles/SRP/AutoImage"));

const SwiperImage = ({ 
    images, 
    showThumbs = false, 
    altImage = "",
    width = 400,
    height = 222,
    widthThumbnail = 70,
    heightThumbnail = 38,
    useNavigation = false,
    slidesPerView = 4,
    slidesPerViewThumbail = 10,
    loader = cloudinaryLoaderVehicleImages,
    effect="fade",
    usePagination= false,
    showFullScreen = true,
    isSRPage = false,
    shouldResizeImg = false,
}: 
  { 
  images: string[], 
  showThumbs?: boolean,
  altImage: string,
  width?: number,
  height?: number,
  widthThumbnail?: number,
  heightThumbnail?: number,
  useNavigation?: boolean,
  slidesPerView?: number,
  slidesPerViewThumbail?: number
  loader?: any,
  effect?: 'slide' | 'fade' | 'cube' | 'coverflow' | 'flip' | 'creative' | 'cards',
  usePagination?: boolean,
  showFullScreen?: boolean,
  isSRPage?: boolean,
  shouldResizeImg?: boolean,
}) => {
  
    const [thumbsSwiper, setThumbsSwiper] = useState<SwiperInstance>();
    const [showFullScreenPopup, setShowFullScreenPopup] = useState(false);
    const [swiper, setSwiper] = useState<SwiperInstance>();
    const { whichDevice } = useResize();

    const isMobileDevice = whichDevice === DeviceEnum.MOBILE;

    useEffect(() => {
        setTimeout(() => {
            if (swiper && !swiper.destroyed) {
                swiper?.update();
            }
        }, 500);
    }, [swiper]);

    const goFullScreen = () => {
        setShowFullScreenPopup(true);
    };

    const onExitFullScreen = () =>{
        setShowFullScreenPopup(false);
    };

    return (
        <>
            <Swiper
                initialSlide={0}
                effect={effect}
                spaceBetween={10}
                slidesPerView={slidesPerView}
                loop={false}
                navigation={useNavigation}
                pagination={usePagination}
                modules={[EffectFade, Navigation, Pagination, Thumbs]}
                className="gallery-top fotorama" // fotorama is used for lotlinx script please don't remove it
                autoHeight
                observer
                observeParents
                onSwiper={setSwiper}
                thumbs={{ swiper: thumbsSwiper }}
            >
                {showFullScreen &&
                    <div 
                        onClick={goFullScreen}
                        className="fullscreen-action"><i className="fa" aria-hidden="true">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff" xmlns="http://www.w3.org/2000/svg"> <path d="M2 7V2H7" stroke="#007aff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/> <path d="M22 7V2H17" stroke="#007aff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/> <path d="M7 22L2 22L2 17" stroke="#007aff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/> <path d="M17 22L22 22L22 17" stroke="#007aff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/> </svg>
                        </i>
                    </div>
                }
                {images.filter((c, index)=> index > 0).map((item: string, index: number) => (
                    <SwiperSlide key={uuidv4()}>
                        <AutoImage 
                            src={item}
                            alt={altImage}
                            width={width}
                            height={height}
                            loader={loader}
                            loadingType={ isSRPage ? index === 0? "eager" : "lazy" :  (index<= 3 || index - images.length <=3 ? "eager" : "lazy")}
                            priority={index === 0}
                            shouldResizeImg={shouldResizeImg}
                        />
                    </SwiperSlide>
                ))}
            </Swiper>
            {showThumbs &&
            <Swiper
                initialSlide={0}
                onSwiper={setThumbsSwiper}
                spaceBetween={10}
                slidesPerView={slidesPerViewThumbail}
                freeMode={true}
                watchSlidesProgress={true}
                modules={[FreeMode, Navigation, Pagination, Thumbs]}
                className="gallery-thumbs"
                observer
                observeParents
            >
                {images.filter((c, index)=> index > 0).map((item: string, index: number) => (
                    <SwiperSlide key={uuidv4()}>
                        <AutoImage 
                            loader={cloudinaryLoaderVDPThumbnailImages}
                            src={item}
                            alt={altImage}
                            width={widthThumbnail}
                            height={heightThumbnail}
                            loadingType={index<= 3 || index - images.length <=3 ? "eager" : "lazy"}
                            shouldResizeImg={shouldResizeImg}
                        />
                    </SwiperSlide>
                ))}
            </Swiper>
            }

            {showFullScreen && showFullScreenPopup &&
            <Modal className="modal-fullscreen"
                backdropClassName="backdrop-fullscreen"
                fade={false}
                external={<div onClick={onExitFullScreen} className="close-fullscreen"><i className="fa fa-times" aria-hidden="true"></i></div>}
                isOpen={true} backdrop="static" toggle={() => onExitFullScreen()}>
                <ModalBody>
                    <div className="fullscreen-instruction"><i className="fa fa-level-down" aria-hidden="true"></i> scroll down to see more pictures</div>
                    {images.filter((c, index)=> index > 0).map((item: string) => {
                        const widthDevice = isMobileDevice ? 768 : 1080;
                        const loaderFunction = isMobileDevice ? cloudinaryLoaderVDPImagesMobile : cloudinaryLoaderVDPImages;
                        return (
                            <AutoImage
                                key={uuidv4()}
                                src={item}
                                alt={altImage}
                                width={widthDevice}
                                height={height}
                                loader={loaderFunction}
                                loadingType="lazy"
                                shouldResizeImg={shouldResizeImg}
                            />
                        );
                    })}
                </ModalBody>
            </Modal>
            }
        </>
    );
};

export default SwiperImage;
